import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import { Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDocumentVersions } from "@/hooks/useDocuments";
import { useEmployeePickerNames } from "@/hooks/useEmployees";
import { formatDateTime, formatDecimal } from "@/lib/format";
import type { Document } from "@/types/document";

interface DocumentVersionHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: Document | null;
}

function formatFileSize(bytes: number | undefined, locale: string): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${formatDecimal(bytes / 1024, locale, 1)} KB`;
  return `${formatDecimal(bytes / (1024 * 1024), locale, 1)} MB`;
}

export function DocumentVersionHistory({
  open,
  onOpenChange,
  document,
}: DocumentVersionHistoryProps) {
  const { t, i18n } = useLingui();

  const { data, isLoading } = useDocumentVersions(document?.id ?? "", 50, 0);
  // Clerk user id -> name from the any-module employee picker (#1688), so a
  // documents-only role names the uploader too.
  const { namesByClerkUserId: userNameMap } = useEmployeePickerNames();
  const versions = data?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>
            <Trans id="documents.versions.title">Version History</Trans>
            {document && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                — {document.name}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans id="documents.versions.description">Every uploaded version of this document.</Trans>
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
          </div>
        ) : versions.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Trans id="documents.versions.empty">No version history available</Trans>
          </div>
        ) : (
          <div className="space-y-3">
            {versions.map((version) => {
              const isCurrent = version.version_number === document?.current_version;
              return (
                <div
                  key={version.id}
                  className={`rounded-lg border p-3 ${
                    isCurrent ? "border-primary bg-primary/5" : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        v{version.version_number}
                      </span>
                      {isCurrent && (
                        <Badge variant="default">
                          <Trans id="documents.versions.current">Current</Trans>
                        </Badge>
                      )}
                    </div>
                    {/* KNOWN GAP (issue #1297): per-version downloads are NOT
                        audited. The access log's document_version column records
                        the document's CURRENT version, and there is no
                        per-version download endpoint in this run, so routing
                        this anchor through the audited endpoint would write a
                        row claiming the wrong version. The anchor stays as-is;
                        see docs/guides/document-management.md. */}
                    {version.download_url && (
                      <a
                        href={version.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="sm">
                          <Download className="mr-1 h-3 w-3" aria-hidden="true" />
                          <Trans id="documents.versions.download">Download</Trans>
                        </Button>
                      </a>
                    )}
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                    {version.filename && (
                      <div>
                        <span className="font-medium text-foreground">
                          {t({ id: "documents.fields.file", message: "File" })}:
                        </span>{" "}
                        {version.filename}
                      </div>
                    )}
                    {version.size_bytes !== undefined && (
                      <div>
                        <span className="font-medium text-foreground">
                          {t({ id: "documents.versions.size", message: "Size" })}:
                        </span>{" "}
                        {formatFileSize(version.size_bytes, i18n.locale)}
                      </div>
                    )}
                    {version.created_by && (
                      <div>
                        <span className="font-medium text-foreground">
                          <Trans id="documents.versions.uploadedBy">Uploaded by</Trans>:
                        </span>{" "}
                        {userNameMap.get(version.created_by ?? "") ??
                          t({ id: "common.unknown", message: "Unknown" })}
                      </div>
                    )}
                    {version.created_at && (
                      <div>
                        <span className="font-medium text-foreground">
                          {t({ id: "documents.versions.uploadedAt", message: "Uploaded" })}:
                        </span>{" "}
                        {formatDateTime(version.created_at, i18n.locale)}
                      </div>
                    )}
                  </div>

                  {version.version_note && (
                    <div className="mt-2 text-sm italic text-muted-foreground">
                      {version.version_note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
